// Structural subset of the Gemini Live protocol that the app uses. The SDK's LiveServerMessage is assignable to
// ServerMessage, and recorded fixtures (e2e/fixtures/sessions/*.jsonl) are plain ServerMessage objects.

export type ServerMessage = {
  setupComplete?: object;
  serverContent?: {
    modelTurn?: { parts?: Array<{ inlineData?: { data?: string; mimeType?: string }; text?: string }> };
    turnComplete?: boolean;
    generationComplete?: boolean;
    interrupted?: boolean;
    inputTranscription?: { text?: string };
    outputTranscription?: { text?: string };
  };
  toolCall?: { functionCalls?: Array<{ id?: string; name?: string; args?: Record<string, unknown> }> };
  toolCallCancellation?: { ids?: string[] };
  goAway?: { timeLeft?: string };
  sessionResumptionUpdate?: { newHandle?: string; resumable?: boolean };
};

export type ToolResponsePart = {
  id: string;
  name: string;
  response: Record<string, unknown>;
  // Only honoured for NON_BLOCKING calls.
  scheduling?: 'INTERRUPT' | 'WHEN_IDLE' | 'SILENT';
};

export type ClientSend =
  | { kind: 'text'; text: string }
  | { kind: 'audio'; bytes: number }
  | { kind: 'audioStreamEnd' }
  | { kind: 'toolResponse'; responses: ToolResponsePart[] };

/** One line of a recorded session fixture. Audio payloads are stripped on record (bytes counted instead). */
export type FixtureLine =
  | { dir: 'server'; ms: number; msg: ServerMessage }
  | { dir: 'client'; ms: number; send: ClientSend };

/** Marks the start of a session. The prompt tells the model to greet when it sees this. */
export const SESSION_START_SIGNAL = '[session-start]';

export function stripAudio(msg: ServerMessage): ServerMessage {
  const parts = msg.serverContent?.modelTurn?.parts;
  if (!parts?.some((p) => p.inlineData?.data)) return msg;
  return {
    ...msg,
    serverContent: {
      ...msg.serverContent,
      modelTurn: {
        parts: parts.map((p) =>
          p.inlineData?.data ? { inlineData: { mimeType: p.inlineData.mimeType ?? 'audio/pcm', data: '' } } : p,
        ),
      },
    },
  };
}

// Native-audio transcription sometimes emits control tokens such as "<no speech>", "{pause}" or "--------".
// Never show them.
export function cleanTranscript(text: string): string {
  return text
    .replace(/<no speech>/gi, '')
    .replace(/\{[a-z_ ]+\}/gi, '')
    .replace(/-{3,}/g, '')
    .replace(/\s{2,}/g, ' ');
}
